import type { Media } from '@/payload-types'
import { extractCaptionPlainText } from '@/utilities/mediaImageObject'
import { resolveMediaImageAlt } from '@/utilities/resolveMediaImageAlt'

const GENERIC_ALT =
  /^(imagem|image|photo|foto|picture|img|untitled|figura|figure|grafico|gráfico|screenshot|screen|pic|thumbnail)$/i

/** Remove espaços invisíveis e normaliza espaços (ALT do título do post). */
export function cleanTitleForImageAlt(title: string): string {
  return title
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

function normalizedLoose(s: string): string {
  return cleanTitleForImageAlt(s).toLowerCase()
}

export function altTextEquals(a: string, b: string): boolean {
  return normalizedLoose(a) === normalizedLoose(b)
}

/**
 * ALT fraco ou genérico (evitar lixo do CMS / placeholders).
 */
export function isWeakAltText(value: string): boolean {
  const t = value.trim()
  if (t.length < 2) return true
  if (GENERIC_ALT.test(t)) return true
  if (t.length <= 5 && !/[aeiouáéíóúàèìòùâêîôûãõ]/i.test(t)) return true
  return false
}

const MAX_ALT = 125
const MAX_CAPTION = 240

export function truncateForSeo(text: string, max: number): string {
  const s = text.trim()
  if (s.length <= max) return s
  const cut = s.slice(0, max - 1).trimEnd()
  const lastSpace = cut.lastIndexOf(' ')
  const base = lastSpace > 40 ? cut.slice(0, lastSpace) : cut
  return `${base}…`
}

/**
 * ALT para imagens no corpo do artigo:
 * 1) `media.alt` do Payload (inalterado se existir e for útil)
 * 2) heading mais próximo (H1–H3 em ordem de leitura)
 * 3) título do post
 * 4) fallback a partir do ficheiro / frase editorial (via `resolveMediaImageAlt`)
 */
export function resolvePostContentImageAlt(
  media: Media,
  opts: { nearestHeading?: string | null; postTitle?: string | null },
): string {
  const raw = media.alt?.trim() ?? ''
  if (raw.length > 0 && !isWeakAltText(raw)) {
    return truncateForSeo(raw, MAX_ALT)
  }

  const h = opts.nearestHeading?.trim() ?? ''
  if (h.length > 0 && !isWeakAltText(h)) {
    return truncateForSeo(h, MAX_ALT)
  }

  const title = opts.postTitle?.trim() ?? ''
  if (title.length > 0) {
    return truncateForSeo(cleanTitleForImageAlt(title), MAX_ALT)
  }

  const fromFile = resolveMediaImageAlt(media)
  if (!isWeakAltText(fromFile)) return truncateForSeo(fromFile, MAX_ALT)

  return 'Ilustração editorial de apoio ao conteúdo do artigo'
}

const PROVIDER_LABELS: Record<string, string> = {
  pexels: 'Pexels',
  unsplash: 'Unsplash',
  pixabay: 'Pixabay',
  freepik: 'Freepik',
}

export type ImageCredit = {
  authorName: string | null
  authorUrl: string | null
  providerLabel: string | null
  sourceUrl: string | null
}

/**
 * Crédito/fonte da imagem a partir de `media.imageSource` (preenchido pelo Banco de Imagens).
 * `null` quando não há fonte a creditar: provider 'none' (imports antigos) ou 'own' sem autor.
 * O rótulo ("Imagem:") e o render dos links ficam no componente (i18n + JSX) — aqui só os dados.
 */
export function buildImageCredit(media: Media): ImageCredit | null {
  const src = media.imageSource
  if (!src) return null
  const provider = (src.provider ?? 'none').toLowerCase()
  if (provider === 'none') return null
  const authorName = src.authorName?.trim() || null
  const authorUrl = src.authorUrl?.trim() || null
  const sourceUrl = src.sourceUrl?.trim() || null
  const providerLabel = PROVIDER_LABELS[provider] ?? null
  if (provider === 'own' && !authorName) return null
  if (!providerLabel && !authorName) return null
  return { authorName, authorUrl, providerLabel, sourceUrl }
}

/**
 * Legenda visível que **complementa** o ALT (não deve repetir o mesmo texto).
 */
export function buildHeroFigcaptionText(args: {
  alt: string
  media: Media
  subtitle?: string | null
  metaDescription?: string | null
}): string {
  const cap = extractCaptionPlainText(args.media.caption)
  if (cap.length > 0 && !altTextEquals(cap, args.alt)) {
    return truncateForSeo(cap, MAX_CAPTION)
  }
  const sub = args.subtitle?.trim() ?? ''
  if (sub.length > 0 && !altTextEquals(sub, args.alt)) {
    return truncateForSeo(sub, MAX_CAPTION)
  }
  const meta = args.metaDescription?.trim() ?? ''
  // Ignora metas que são templates Yoast não resolvidos (ex.: "%%resumo%%") importados do WP —
  // senão a legenda mostra o token cru. Cai na âncora editorial abaixo.
  if (meta.length > 0 && !/%%[a-z0-9_]+%%/i.test(meta) && !altTextEquals(meta, args.alt)) {
    return truncateForSeo(meta, MAX_CAPTION)
  }
  // Sem legenda/subtítulo/meta: se a imagem tem crédito (fonte do banco), devolve só o TÍTULO do artigo
  // (sem o prefixo "Imagem editorial em destaque do artigo") — o PostImage o exibe na MESMA linha, após
  // o crédito ("Fonte: autor/provedor — {título}"). Senão (sem crédito), mantém a âncora descritiva.
  if (buildImageCredit(args.media)) return truncateForSeo(cleanTitleForImageAlt(args.alt), MAX_CAPTION)
  return truncateForSeo(`Imagem editorial em destaque do artigo «${args.alt}».`, MAX_CAPTION)
}

export function buildContentFigcaptionText(args: {
  alt: string
  captionPlainFromCms: string
  nearestHeading?: string | null
  postTitle?: string | null
}): string {
  const cap = args.captionPlainFromCms.trim()
  if (cap.length > 0 && !altTextEquals(cap, args.alt)) {
    return truncateForSeo(cap, MAX_CAPTION)
  }
  const h = args.nearestHeading?.trim() ?? ''
  if (h.length > 0 && !altTextEquals(h, args.alt)) {
    return truncateForSeo(`Contexto visual associado à secção «${h}».`, MAX_CAPTION)
  }
  const t = args.postTitle?.trim() ?? ''
  if (t.length > 0 && !altTextEquals(t, args.alt)) {
    // Removido a pedido do dono (2026-07-11): o texto "Imagem de apoio ao artigo «título»." não fazia
    // sentido → neste caso a imagem fica SEM legenda automática (as demais camadas ficam intactas).
    return ''
  }
  return 'Ilustração de apoio ao conteúdo editorial do artigo.'
}

export function buildVideoFigcaptionText(args: {
  captionPlainFromCms: string
  postTitle?: string | null
}): string {
  const cap = args.captionPlainFromCms.trim()
  if (cap.length > 0) return truncateForSeo(cap, MAX_CAPTION)
  const t = args.postTitle?.trim() ?? ''
  if (t.length > 0) {
    return truncateForSeo(`Vídeo de apoio ao artigo «${t}».`, MAX_CAPTION)
  }
  return 'Vídeo de apoio ao conteúdo editorial do artigo.'
}
